/**
 * 기존 안전문서 이관(Import) 모달 — KRAS·자체 양식 위험성평가표를 Heinrich 로.
 *
 * 흐름: 파일 선택 → 백그라운드 분석(폴링, 30문서 ≈ 5~10분) → 검토(문서별 항목
 * 체크·작업명/평가일 수정) → 반영. 반영되는 순간 사고 반영·법령 영향·상시평가·
 * 증빙 PDF·조치 추적·의무 캘린더에 전부 물린다(백엔드 정규화 저장).
 * 분석은 전량 로컬 처리(결정적 파싱 + 로컬 LLM 판정) — 문서가 외부로 나가지 않는다.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { FiCheck, FiChevronDown, FiChevronRight, FiFile, FiSearch, FiUpload } from 'react-icons/fi'
import Modal from '../../components/ui/Modal'
import Button from '../../components/ui/Button'
import Spinner from '../../components/ui/Spinner'
import { toast } from '../../hooks/useToast'
import { importsApi } from '../../lib/importsApi'
import {
  docImportApi, type DiagnoseStatus, type ImportDoc, type ImportJobStatus,
} from '../../lib/riskAssessmentApi'

const ACCEPT = '.xlsx,.xlsm,.xls,.docx,.doc,.pdf,.hwp,.hwpx,.csv'
const MAX_FILES = 30

/** 저장까지 지원하는 종류 — 백엔드 SUPPORTED_KINDS 와 같은 목록. */
const KIND_LABEL: Record<string, string> = {
  risk_assessment: '위험성평가',
  jsa: 'JSA',
  work_permit: '작업허가서',
  tbm: 'TBM',
  checklist: '점검표',
  training: '교육일지',
  site_overview: '사업장 개요',
}
const KIND_TONE: Record<string, string> = {
  risk_assessment: 'bg-brand-primary/10 text-brand-primary border-brand-primary/30',
  jsa: 'bg-sky-500/10 text-sky-600 dark:text-sky-400 border-sky-500/30',
  work_permit: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/30',
  tbm: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30',
  site_overview: 'bg-violet-500/10 text-violet-600 dark:text-violet-400 border-violet-500/30',
}
/**
 * 업로드 **칸**. 사용자가 1차 분류를 해 주면 종류 오판 자체가 사라진다
 * (실측: 자동 판별이 점검표형 평가표를 오분류했다). 칸을 강제하지는 않는다 —
 * 자동 판별이 다르게 보면 검토 화면에서 되묻는다.
 */
const SLOTS = [
  { key: 'risk_assessment', label: '위험성평가',
    hint: 'KRAS·자체 양식 평가표' },
  { key: 'jsa', label: 'JSA (작업안전분석)',
    hint: '작업 단계별 위험·안전조치' },
  { key: 'work_permit', label: '작업허가서',
    hint: '화기·밀폐공간·고소 등 PTW' },
  // KRAS 가 별도 문서로도 내보내는 '사업장 개요(안전보건상 위험정보)' —
  // 설비·화학물질 현황을 뽑아 대장과 이후 문서 생성 문맥에 쓴다(2026-08-20).
  { key: 'site_overview', label: '사업장 개요·위험정보',
    hint: 'KRAS 사업장 개요 등 설비·화학물질 현황' },
  { key: 'auto', label: '종류를 모르겠어요',
    hint: 'AI가 종류부터 판별합니다' },
] as const
type SlotKey = (typeof SLOTS)[number]['key']
const EMPTY_SLOTS: Record<SlotKey, File[]> =
  { risk_assessment: [], jsa: [], work_permit: [], site_overview: [], auto: [] }

/** 작업허가서 상태 — 기간을 못 읽었을 때 사용자가 고른다. */
const PERMIT_STATUS = [
  { v: 'closed', t: '종료된 허가서' },
  { v: 'active', t: '진행 중' },
  { v: 'approved', t: '승인·미착수' },
] as const

/** 이 문서에서 실제로 저장될 게 있는가(종류마다 페이로드가 다르다). */
function payloadCount(d: ImportDoc): number {
  if (d.doc_kind === 'jsa') return (d.jsa_rows || []).length
  if (d.doc_kind === 'work_permit') return d.permit ? 1 : 0
  if (d.doc_kind === 'tbm') return (d.tbms || []).length
  if (d.doc_kind === 'site_overview') {
    return (d.profile?.equipment?.length || 0) + (d.profile?.chemicals?.length || 0)
  }
  return (d.items || []).length
}
function payloadUnit(d: ImportDoc): string {
  if (d.doc_kind === 'jsa') return '단계'
  if (d.doc_kind === 'work_permit') return '건'
  if (d.doc_kind === 'tbm') return '일지'
  if (d.doc_kind === 'site_overview') return '개 설비·물질'
  return '항목'
}

/**
 * 반영으로 실제 바뀌는 화면들의 쿼리 키.
 *
 * 이전에는 ['risk-assessments'] · ['site-tasks'] 를 무효화했는데 **그 키를 구독하는
 * 화면이 하나도 없었다**(실사용 키는 ra-list / tasks-overview). 그래서 반영에
 * 성공해도 목록이 그대로라 사용자가 실패로 오인하고 재시도 → 중복 반영 위험이 있었다.
 *
 * 백엔드 apply_import 는 RA·항목·작업 생성에 더해 의무 캘린더(SiteDocument
 * last_done_at + recompute_obligations_for_site)와 활동 로그까지 건드리고,
 * build_profile 이면 사업장 유해인자·장비까지 바꾸므로 그 화면들도 함께 갱신한다.
 */
const AFFECTED_KEYS = [
  'ra-list',            // 위험성평가 목록
  'tasks-overview',     // 작업 보드
  'task-docs',          // 작업별 문서 펼침
  'measures-due',       // 개선조치 추적
  'compliance',         // 법정 의무 캘린더
  'penalty-exposure',   // 과태료 노출
  'today',              // 오늘 홈
  'site-hub',           // 사업장 허브 요약
  'site-activity',      // 활동 로그
  'sites',              // 사업장 프로필(유해인자·장비)
] as const

type Phase = 'pick' | 'analyzing' | 'review' | 'applying' | 'done'

export default function DocImportModal({ siteId, open, onClose, onApplied }: {
  siteId: string
  open: boolean
  onClose: () => void
  onApplied?: () => void
}) {
  const qc = useQueryClient()
  const [phase, setPhase] = useState<Phase>('pick')
  const [slots, setSlots] = useState<Record<SlotKey, File[]>>(EMPTY_SLOTS)
  // 업로드 순서 = 칸 순서. kinds 배열을 files 와 1:1 로 맞추려면 한 곳에서 만든다.
  const picked = useMemo(
    () => SLOTS.flatMap(sl => slots[sl.key].map(f => ({ file: f, slot: sl.key }))),
    [slots])
  const files = useMemo(() => picked.map(x => x.file), [picked])
  const [jobId, setJobId] = useState<string | null>(null)
  const [job, setJob] = useState<ImportJobStatus | null>(null)
  const [docs, setDocs] = useState<ImportDoc[]>([])
  // 문서/항목 선택 상태 — key = `${docIdx}` / `${docIdx}:${itemIdx}`
  const [docOpen, setDocOpen] = useState<Record<number, boolean>>({})
  const [unchecked, setUnchecked] = useState<Record<string, boolean>>({})
  const [buildProfile, setBuildProfile] = useState(true)
  const [result, setResult] = useState<Awaited<ReturnType<typeof docImportApi.apply>> | null>(null)
  const pollRef = useRef<number | null>(null)
  // 진단(2층 법규 대조 + 3층 사고 대조) — 반영 직후 자동 시작
  const [diag, setDiag] = useState<DiagnoseStatus | null>(null)
  const [diagJob, setDiagJob] = useState<string | null>(null)
  const [diagGate, setDiagGate] = useState<{ preview: boolean; deep_limit: number } | null>(null)
  const [diagErr, setDiagErr] = useState<string | null>(null)
  // 남은 건수는 Smart Import 와 **같은 풀**이라 같은 엔드포인트를 쓴다.
  // 지금까지 이 화면엔 표시가 없어, 사용자가 한도를 초과할 때까지 몰랐다.
  const [quota, setQuota] = useState<{
    plan_code: string; limit_files: number; used_files: number
    remaining: number; window: string } | null>(null)
  const diagPollRef = useRef<number | null>(null)

  useEffect(() => {
    if (!open) {
      setPhase('pick'); setSlots(EMPTY_SLOTS); setJobId(null); setJob(null)
      // 이관(RA·JSA·PTW)은 '자료 가져오기' 풀을 쓴다(MSDS 는 별도 풀).
      importsApi.quota().then(setQuota).catch(() => setQuota(null))
      setDocs([]); setUnchecked({}); setResult(null)
      setDiag(null); setDiagJob(null); setDiagGate(null); setDiagErr(null)
      if (pollRef.current) window.clearInterval(pollRef.current)
      if (diagPollRef.current) window.clearInterval(diagPollRef.current)
    }
  }, [open])

  // 진단 폴링 — 반영이 끝나면 자동으로 돈다. 상태는 서버(DB)에 있어 새로고침에도 안전.
  useEffect(() => {
    if (!diagJob) return
    const tick = async () => {
      try {
        const st = await docImportApi.diagnoseStatus(siteId, diagJob)
        setDiag(st)
        if (st.status !== 'running' && diagPollRef.current) {
          window.clearInterval(diagPollRef.current)
        }
      } catch { /* 다음 tick 재시도 */ }
    }
    tick()
    diagPollRef.current = window.setInterval(tick, 4000)
    return () => { if (diagPollRef.current) window.clearInterval(diagPollRef.current) }
  }, [diagJob, siteId])

  // 분석 폴링 — 상태는 전부 서버(DB)에 있으므로 새로고침해도 잡은 계속 돈다
  useEffect(() => {
    if (phase !== 'analyzing' || !jobId) return
    const tick = async () => {
      try {
        const st = await docImportApi.status(siteId, jobId)
        setJob(st)
        if (st.status === 'done' || st.status === 'error') {
          if (pollRef.current) window.clearInterval(pollRef.current)
          // 위험성평가만 items 를 갖는다 — 종류별 페이로드로 판단해야
          // JSA·허가서만 올린 사용자가 "인식 실패"를 보지 않는다.
          const usable = st.docs.filter(d => payloadCount(d) > 0)
          setDocs(st.docs)
          // 일부만 읽힌 문서·이미 이관한 문서(중복)는 **기본 해제**.
          // partial 은 그대로 반영하면 원본 행이 조용히 빠지고,
          // duplicate 는 같은 문서가 두 번 저장돼 쿼터만 깎인다.
          setUnchecked(Object.fromEntries(
            st.docs.map((d, i) => [`${i}`, !!d.partial || !!d.duplicate]).filter(([, v]) => v)))
          setDocOpen(Object.fromEntries(st.docs.map((_, i) => [i, st.docs.length <= 3])))
          if (usable.length === 0) {
            toast.error('가져올 수 있는 문서가 없습니다. 위험성평가·JSA·작업허가서 중 하나인지 확인해주세요.')
            setPhase('pick')
          } else {
            setPhase('review')
          }
        }
      } catch { /* 폴링 실패는 다음 tick 에서 재시도 */ }
    }
    tick()
    pollRef.current = window.setInterval(tick, 4000)
    return () => { if (pollRef.current) window.clearInterval(pollRef.current) }
  }, [phase, jobId, siteId])

  async function startAnalyze() {
    if (files.length === 0) return
    try {
      setPhase('analyzing')
      const kinds = picked.map(x => (x.slot === 'auto' ? '' : x.slot)) as
        ('' | 'risk_assessment' | 'jsa' | 'work_permit' | 'site_overview')[]
      const r = await docImportApi.analyze(siteId, files, { kinds })
      setJobId(r.job_id)
    } catch (e: any) {
      setPhase('pick')
      // 쿼터 초과(402) 같은 서버 사유를 삼키지 않는다 — 지금까지 "파일 형식을
      // 확인해주세요"만 떠서, 한도에 걸린 사용자가 파일만 계속 바꿔 보게 됐다.
      const detail = e?.response?.data?.detail
      toast.error(typeof detail === 'string' && detail
        ? detail
        : '분석 시작에 실패했습니다. 파일 형식·크기(20MB)를 확인해주세요.')
    }
  }

  const selectedDocs = useMemo(() => {
    return docs
      .map((d, di) => ({
        ...d,
        // 항목 체크 해제는 위험성평가에만 있다(다른 종류는 문서 단위 선택).
        items: (d.items || []).filter((_, ii) => !unchecked[`${di}:${ii}`]),
      }))
      .filter((d, di) => !unchecked[`${di}`] && payloadCount(d) > 0)
  }, [docs, unchecked])

  const totalItems = selectedDocs.reduce((a, d) => a + payloadCount(d), 0)
  /** 기간을 못 읽어 사용자가 상태를 골라야 하는 허가서가 남아 있는가. */
  const pendingConfirm = useMemo(
    () => selectedDocs.filter(d => d.doc_kind === 'work_permit'
      && d.permit?.needs_confirm && !d.permit?.status_override).length,
    [selectedDocs])

  /** 작업명 기준 묶음 요약 — 사용자가 작업명을 고치면 즉시 다시 묶여 보인다. */
  const groupSummary = useMemo(() => {
    const by = new Map<string, { name: string; kinds: string[]; n: number }>()
    for (const d of docs) {
      if (d.doc_kind === 'site_overview') continue   // 작업 문서가 아니다
      if (payloadCount(d) === 0) continue
      const name = (d.task_name || '미상 작업').trim()
      const g = by.get(name) || { name, kinds: [], n: 0 }
      g.n += 1
      const k = d.doc_kind || 'risk_assessment'
      if (!g.kinds.includes(k)) g.kinds.push(k)
      by.set(name, g)
    }
    return [...by.values()]
  }, [docs])

  async function apply() {
    if (selectedDocs.length === 0 || !jobId) return
    setPhase('applying')
    try {
      const r = await docImportApi.apply(siteId, jobId, selectedDocs, buildProfile)
      setResult(r)
      setPhase('done')
      // task-docs 는 ['task-docs', siteId, taskId] 라 siteId 까지만 주면 접두 일치로
      // 그 사업장의 모든 작업이 함께 갱신된다.
      for (const k of AFFECTED_KEYS) qc.invalidateQueries({ queryKey: [k, siteId] })
      onApplied?.()
      // 반영이 끝나면 곧바로 진단을 건다 — 이관의 값어치는 '옮겨 담기'가 아니라
      // "무엇이 빠졌는지" 알려주는 데 있다. 실패해도 이관 자체는 이미 성공이므로 삼킨다.
      try {
        const ids = [...new Set(r.created.map(c => c.task_id).filter(Boolean))]
        if (ids.length > 0) {
          const d = await docImportApi.diagnose(siteId, ids)
          setDiagGate({ preview: d.preview, deep_limit: d.deep_limit })
          setDiagJob(d.job_id)
        }
      } catch {
        setDiagErr('진단을 시작하지 못했습니다. 작업 보드에서 다시 실행할 수 있습니다.')
      }
    } catch (e: any) {
      setPhase('review')
      const detail = e?.response?.data?.detail
      toast.error(typeof detail === 'string' && detail
        ? detail
        : '반영에 실패했습니다. 잠시 후 다시 시도해주세요.')
    }
  }

  function patchDoc(di: number, patch: Partial<ImportDoc>) {
    setDocs(prev => prev.map((d, i) => (i === di ? { ...d, ...patch } : d)))
  }

  return (
    <Modal isOpen={open} onClose={phase === 'applying' ? () => {} : onClose} size="xl"
           title="기존 안전문서 가져오기">
      {phase === 'pick' && (
        <div className="space-y-4">
          <p className="text-sm text-ink-muted">
            엑셀·한글(HWP)·워드·PDF 문서를 <b className="text-ink">종류별 칸</b>에 올려 주세요.
            양식이 우리와 달라도 AI가 내용을 읽어 Heinrich 형식으로 채웁니다.
            가져온 자료는 <b className="text-ink">사고 반영·상시평가·증빙 PDF·개선조치 추적</b>에
            바로 연결됩니다. 분석은 전량 서버 내 로컬 처리로, 문서가 외부로 나가지 않습니다.
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            {SLOTS.map(sl => {
              const mine = slots[sl.key]
              return (
                <div key={sl.key}
                     className={`rounded-xl border-2 border-dashed p-3 transition-colors ${
                       mine.length ? 'border-brand-primary/50 bg-brand-primary/5'
                                   : 'border-surface-border'}`}>
                  <label className="flex cursor-pointer flex-col gap-1">
                    <span className="flex items-center gap-2 text-sm font-medium text-ink">
                      <FiUpload className="text-ink-muted" />
                      {sl.label}
                      {mine.length > 0 && (
                        <span className="rounded-full bg-brand-primary/15 px-2 py-0.5 text-xs
                                         text-brand-primary">{mine.length}</span>
                      )}
                    </span>
                    <span className="text-xs text-ink-faint">{sl.hint}</span>
                    <input type="file" multiple accept={ACCEPT} className="hidden"
                           onChange={e => {
                             const add = Array.from(e.target.files || [])
                             e.target.value = ''   // 같은 파일 다시 고를 수 있게
                             setSlots(prev => {
                               const used = SLOTS.reduce((n, x) => n + prev[x.key].length, 0)
                               const room = Math.max(0, MAX_FILES - used)
                               if (add.length > room) {
                                 toast.error(`한 번에 ${MAX_FILES}개까지 올릴 수 있습니다.`)
                               }
                               return { ...prev, [sl.key]: [...prev[sl.key], ...add.slice(0, room)] }
                             })
                           }} />
                  </label>
                  {mine.length > 0 && (
                    <ul className="mt-2 max-h-24 space-y-1 overflow-y-auto text-xs">
                      {mine.map((f, i) => (
                        <li key={`${f.name}-${i}`} className="flex items-center gap-1.5 text-ink">
                          <FiFile className="shrink-0 text-ink-muted" />
                          <span className="truncate">{f.name}</span>
                          <button type="button" aria-label={`${f.name} 제거`}
                                  className="ml-auto shrink-0 text-ink-faint hover:text-red-500"
                                  onClick={() => setSlots(prev => ({
                                    ...prev,
                                    [sl.key]: prev[sl.key].filter((_, j) => j !== i),
                                  }))}>✕</button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )
            })}
          </div>
          <p className="text-xs text-ink-faint">
            최대 {MAX_FILES}개 · 개당 20MB · {ACCEPT}
          </p>
          {quota && (
            <div className={`rounded-lg border px-3 py-2 text-xs ${
              quota.limit_files >= 0 && files.length > quota.remaining
                ? 'border-red-500/40 bg-red-500/5 text-red-600 dark:text-red-400'
                : 'border-surface-border bg-surface-raised text-ink-muted'}`}>
              {quota.limit_files < 0 ? (
                <>현재 <b className="text-ink">{quota.plan_code.toUpperCase()}</b> 플랜 — 가져오기 무제한</>
              ) : (
                <>
                  현재 <b className="text-ink">{quota.plan_code.toUpperCase()}</b> 플랜 ·
                  {quota.window === 'lifetime' ? ' 누적' : ' 이번 달'} {quota.limit_files}개 중{' '}
                  <b className="text-ink">{quota.remaining}개</b> 남았습니다
                  {files.length > quota.remaining && (
                    <> — 지금 고른 {files.length}개는 한도를 넘습니다.</>
                  )}
                </>
              )}
              <span className="block mt-0.5 text-ink-faint">
                교육 이수·보호구 대장·자격증 등 다른 자료 가져오기와 한도를 <b>함께</b> 씁니다
                (MSDS·화학물질은 별도 한도).
              </span>
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={onClose}>취소</Button>
            <Button
              disabled={files.length === 0 ||
                (!!quota && quota.limit_files >= 0 && files.length > quota.remaining)}
              onClick={startAnalyze}>
              분석 시작 ({files.length}개)
            </Button>
          </div>
        </div>
      )}

      {phase === 'analyzing' && (
        <div className="space-y-3 py-4">
          <div className="flex items-center gap-3">
            <Spinner size="sm" />
            <p className="text-sm text-ink">
              문서를 분석하고 있습니다 — 파일이 많으면 수 분 걸립니다. 창을 닫아도 분석은 계속됩니다.
            </p>
          </div>
          <ul className="space-y-1 text-sm max-h-64 overflow-y-auto">
            {(job?.steps || files.map(f => ({ key: f.name, status: 'pending' as const, note: undefined }))).map(s => (
              <li key={s.key} className="flex items-center gap-2">
                {s.status === 'done' ? <FiCheck className="text-emerald-500 shrink-0" />
                  : s.status === 'error' ? <span className="text-red-500 shrink-0">✕</span>
                  : s.status === 'running' ? <Spinner size="sm" />
                  : <span className="w-4 shrink-0" />}
                <span className="truncate text-ink">{s.key}</span>
                {s.note && <span className="text-xs text-ink-faint shrink-0">{s.note}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* 반영 진행 — "반영 중…" 버튼만 돌면 멈춘 것처럼 보여 새로고침을 부른다(실측 ~9초).
          서버 진행 이벤트는 없으므로 정직한 단계 안내 배너를 목록 위에 고정한다. */}
      {phase === 'applying' && (
        <div className="sticky top-0 z-10 mb-3 flex items-center gap-3 rounded-xl border border-brand-primary/30 bg-surface-card px-4 py-2.5 shadow-md">
          <Spinner size="sm" />
          <div className="text-[12px] text-ink">
            <b>반영하고 있어요</b> — 항목 저장 → 대장·프로필 반영 → 의무 캘린더 갱신
            <span className="ml-1 text-ink-faint">(보통 10초 안팎, 창을 닫지 마세요)</span>
          </div>
        </div>
      )}
      {(phase === 'review' || phase === 'applying') && (
        <div className="space-y-3">
          <p className="text-sm text-ink-muted">
            인식 결과를 확인하고 반영할 문서를 선택하세요. <strong className="text-ink">작업명</strong>은
            파일명이 아니라 문서 안에서 읽은 값이며, 같은 작업으로 묶인 문서는 같은 작업명을 갖습니다.
            필요하면 여기서 고치세요.
          </p>
          {groupSummary.length > 1 && (
            <div className="rounded-xl border border-surface-border bg-surface-raised/50 p-3">
              <p className="text-xs font-semibold text-ink-muted mb-1.5">작업별 묶음</p>
              <div className="flex flex-wrap gap-1.5">
                {groupSummary.map(g => (
                  <span key={g.name}
                        className="rounded-full border border-surface-border bg-surface-base px-2 py-0.5 text-xs text-ink">
                    {g.name}
                    <span className="text-ink-muted"> · {g.kinds.map(k => KIND_LABEL[k] || k).join('/')}</span>
                    <span className="text-ink-muted"> {g.n}건</span>
                  </span>
                ))}
              </div>
            </div>
          )}
          <div className="max-h-[55vh] overflow-y-auto space-y-2 pr-1">
            {docs.map((d, di) => {
              const n = payloadCount(d)
              const usable = n > 0
              const kind = d.doc_kind || (usable ? 'risk_assessment' : '')
              const docOff = !!unchecked[`${di}`] || !usable
              const nSel = (d.items || []).filter((_, ii) => !unchecked[`${di}:${ii}`]).length
              return (
                <div key={d.filename + di}
                     className={`rounded-xl border border-surface-border ${docOff ? 'opacity-60' : ''}`}>
                  <div className="flex items-center gap-2 px-3 py-2">
                    {usable && (
                      <input type="checkbox" checked={!unchecked[`${di}`]}
                             onChange={e => setUnchecked(p => ({ ...p, [`${di}`]: !e.target.checked }))} />
                    )}
                    <button className="flex items-center gap-2 flex-1 min-w-0 text-left"
                            onClick={() => setDocOpen(p => ({ ...p, [di]: !p[di] }))}>
                      {docOpen[di] ? <FiChevronDown className="shrink-0" /> : <FiChevronRight className="shrink-0" />}
                      {kind && (
                        <span className={`shrink-0 rounded-md border px-1.5 py-0.5 text-[11px] font-semibold ${KIND_TONE[kind] || 'bg-surface-raised text-ink-muted border-surface-border'}`}>
                          {KIND_LABEL[kind] || kind}
                        </span>
                      )}
                      <span className="truncate text-sm font-semibold text-ink">{d.filename}</span>
                      {usable ? (
                        <span className="text-xs text-ink-muted shrink-0">
                          {kind === 'risk_assessment'
                            ? `${d.method === 'three_level' ? '3단계' : `빈도·강도${d.matrix_size ? ` ${d.matrix_size}` : ''}`} · ${nSel}/${n}개 항목`
                            : `${n}${payloadUnit(d)}`}
                        </span>
                      ) : (
                        <span className="text-xs text-amber-600 dark:text-amber-400 shrink-0 truncate">
                          {(d.warnings || [])[0] || '인식 실패'}
                        </span>
                      )}
                      {d.task_confidence && d.task_confidence !== 'high' && usable && (
                        <span className="shrink-0 rounded-md border border-amber-500/40 bg-amber-500/10 px-1.5 py-0.5 text-[11px] text-amber-600 dark:text-amber-400">
                          작업 확인 필요
                        </span>
                      )}
                      {/* 이미 반영한 것과 같은 파일 — 다시 반영하면 중복 저장 + 쿼터 차감. */}
                      {d.duplicate && (
                        <span className="shrink-0 rounded-md border border-amber-500/40 bg-amber-500/10 px-1.5 py-0.5 text-[11px] font-semibold text-amber-600 dark:text-amber-400"
                              title="이전에 이미 이관을 완료한 것과 동일한 파일입니다. 다시 반영하면 같은 문서가 중복 저장됩니다.">
                          이미 이관한 문서
                        </span>
                      )}
                      {/* 원본 행이 일부만 들어온 상태 — 그대로 반영하면 조용히 누락된다. */}
                      {d.partial && (
                        <span className="shrink-0 rounded-md border border-red-500/40 bg-red-500/10 px-1.5 py-0.5 text-[11px] font-semibold text-red-600 dark:text-red-400">
                          {d.missing_rows ? `${d.missing_rows}행 누락` : '일부만 읽힘'}
                        </span>
                      )}
                      {/* 사용자가 고른 칸과 자동 판별이 다르다 — 바꾸지 않고 알리기만 한다. */}
                      {d.suggested_kind && d.suggested_kind !== d.doc_kind && (
                        <span className="shrink-0 rounded-md border border-sky-500/40 bg-sky-500/10 px-1.5 py-0.5 text-[11px] text-sky-600 dark:text-sky-400">
                          {KIND_LABEL[d.suggested_kind] || d.suggested_kind}일 수도
                        </span>
                      )}
                      {(() => {
                        const nEq = d.profile?.equipment?.length || 0
                        const nCh = d.profile?.chemicals?.length || 0
                        return (nEq > 0 || nCh > 0) && (
                          <span className="shrink-0 rounded-md border border-violet-500/40 bg-violet-500/10 px-1.5 py-0.5 text-[11px] text-violet-600 dark:text-violet-400"
                                title="문서에서 감지한 사업장 프로필입니다. 화학물질은 유해인자 대장에 등록되고, 설비는 위험설비만 장비 대장에 오릅니다(사무기기 등은 문서 생성 문맥으로만 활용).">
                            프로필 감지{nEq > 0 ? ` · 설비 ${nEq}` : ''}{nCh > 0 ? ` · 화학 ${nCh}` : ''}
                          </span>
                        )
                      })()}
                      {(d.items || []).some(it => it.legal_text) && (
                        <span className="shrink-0 rounded-md border border-surface-border bg-surface-raised px-1.5 py-0.5 text-[11px] text-ink-muted">
                          법적근거 {(d.items || []).filter(it => it.legal_text).length}
                        </span>
                      )}
                      {d.engine === 'llm' && usable && (
                        <span className="shrink-0 rounded-md border border-surface-border bg-surface-raised px-1.5 py-0.5 text-[11px] text-ink-muted">
                          AI 해석
                        </span>
                      )}
                    </button>
                  </div>
                  {/* 접혀 있어도 보여야 하는 경고 — 펼치지 않으면 못 보고 반영한다. */}
                  {(d.partial || (!usable && (d.warnings || []).length > 1)) && (
                    <div className={`border-t px-3 py-2 text-xs ${
                      d.partial ? 'border-red-500/30 bg-red-500/5 text-red-600 dark:text-red-400'
                                : 'border-surface-border text-ink-muted'}`}>
                      {(d.warnings || [])[0]}
                    </div>
                  )}
                  {docOpen[di] && usable && (
                    <div className="border-t border-surface-border px-3 py-2 space-y-2">
                      <div className="flex flex-wrap gap-3">
                        {/* 사업장 개요는 작업과 무관 — 작업명 입력을 보이지 않는다. */}
                        {kind !== 'site_overview' && (
                        <label className="text-xs text-ink-muted flex items-center gap-1.5">
                          작업명
                          <input value={d.task_name || ''} disabled={phase === 'applying'}
                                 onChange={e => patchDoc(di, { task_name: e.target.value })}
                                 className="rounded-lg border border-surface-border bg-transparent px-2 py-1 text-sm text-ink w-44" />
                          <span className="text-[11px] text-ink-muted">
                            {d.task_hint_source === 'head' ? '문서 머리표에서 읽음'
                              : d.task_hint_source === 'content' ? '표 내용에서 추정'
                                : '파일명에서 추정 — 확인 필요'}
                          </span>
                        </label>
                        )}
                        {kind === 'risk_assessment' && (
                          <label className="text-xs text-ink-muted flex items-center gap-1.5">
                            평가일
                            <input type="date" value={d.assessed_at || ''} disabled={phase === 'applying'}
                                   onChange={e => patchDoc(di, { assessed_at: e.target.value })}
                                   className="rounded-lg border border-surface-border bg-transparent px-2 py-1 text-sm text-ink" />
                          </label>
                        )}
                        {kind === 'work_permit' && d.permit && (
                          <label className="text-xs text-ink-muted flex items-center gap-1.5">
                            허가 상태
                            <select
                              value={d.permit.status_override || d.permit.status || ''}
                              disabled={phase === 'applying'}
                              onChange={e => patchDoc(di, {
                                permit: { ...d.permit!, status_override: e.target.value },
                              })}
                              className="rounded-lg border border-surface-border bg-transparent px-2 py-1 text-sm text-ink">
                              {!d.permit.status && !d.permit.status_override && (
                                <option value="">선택하세요</option>
                              )}
                              {PERMIT_STATUS.map(s => (
                                <option key={s.v} value={s.v}>{s.t}</option>
                              ))}
                            </select>
                            <span className="text-[11px] text-ink-muted">
                              {d.permit.work_start_at || d.permit.work_end_at
                                ? `허가기간 ${d.permit.work_start_at || '?'} ~ ${d.permit.work_end_at || '?'}`
                                : '문서에서 허가기간을 못 읽었습니다'}
                            </span>
                          </label>
                        )}
                      </div>

                      {kind === 'risk_assessment' && (
                        <div className="overflow-x-auto">
                          <table className="w-full min-w-[560px] text-xs">
                            <thead>
                              <tr className="text-ink-muted text-left">
                                <th className="py-1 pr-2 w-6"></th>
                                <th className="py-1 pr-2">단위작업</th>
                                <th className="py-1 pr-2">유해위험요인</th>
                                <th className="py-1 pr-2 whitespace-nowrap">위험성</th>
                                <th className="py-1">감소대책</th>
                              </tr>
                            </thead>
                            <tbody>
                              {d.items.map((it, ii) => (
                                <tr key={ii} className={`border-t border-surface-border/60 ${unchecked[`${di}:${ii}`] ? 'opacity-45' : ''}`}>
                                  <td className="py-1 pr-2 align-top">
                                    <input type="checkbox" checked={!unchecked[`${di}:${ii}`]} disabled={phase === 'applying'}
                                           onChange={e => setUnchecked(p => ({ ...p, [`${di}:${ii}`]: !e.target.checked }))} />
                                  </td>
                                  <td className="py-1 pr-2 align-top text-ink whitespace-nowrap max-w-[9rem] truncate">{it.unit_task || '-'}</td>
                                  <td className="py-1 pr-2 align-top text-ink">{it.hazard}</td>
                                  <td className="py-1 pr-2 align-top whitespace-nowrap text-ink">
                                    {it.risk_level || it.risk_text || '-'}
                                    {it.frequency && it.severity ? ` (${it.frequency}×${it.severity})` : ''}
                                    {(it.residual_level || it.residual_score != null || it.residual_text) && (
                                      <span className="text-emerald-600 dark:text-emerald-400">
                                        {' '}→ {it.residual_level || it.residual_text || ''}
                                        {it.residual_score != null ? `(${it.residual_score})` : ''}
                                      </span>
                                    )}
                                    {it.done_at ? ' · 조치완료' : ''}
                                  </td>
                                  <td className="py-1 align-top text-ink-muted">{it.measure || '-'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}

                      {kind === 'jsa' && (
                        <div className="overflow-x-auto">
                          <table className="w-full min-w-[560px] text-xs">
                            <thead>
                              <tr className="text-ink-muted text-left">
                                <th className="py-1 pr-2">작업단계</th>
                                <th className="py-1 pr-2">위험요인</th>
                                <th className="py-1 pr-2">안전조치</th>
                                <th className="py-1 pr-2 whitespace-nowrap">위험성</th>
                                <th className="py-1">담당</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(d.jsa_rows || []).map((r, ii) => (
                                <tr key={ii} className="border-t border-surface-border/60">
                                  <td className="py-1 pr-2 align-top text-ink max-w-[10rem]">{r.step}</td>
                                  <td className="py-1 pr-2 align-top text-ink">{r.hazard}</td>
                                  <td className="py-1 pr-2 align-top text-ink-muted">{r.safeguards || '-'}</td>
                                  <td className="py-1 pr-2 align-top whitespace-nowrap text-ink">
                                    {r.risk_level_before || '-'}
                                    {r.frequency_before && r.severity_before ? ` (${r.frequency_before}×${r.severity_before})` : ''}
                                  </td>
                                  <td className="py-1 align-top text-ink-muted">{r.pic || '-'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}

                      {kind === 'work_permit' && d.permit && (
                        <div className="space-y-1.5 text-xs">
                          <p className="text-ink">
                            <span className="text-ink-muted">허가번호</span> {d.permit.permit_no || '-'}
                            <span className="text-ink-muted"> · 유형</span> {d.permit.permit_type}
                            {d.permit.location && <><span className="text-ink-muted"> · 장소</span> {d.permit.location}</>}
                          </p>
                          <p className="text-ink-muted">
                            안전조치 {d.permit.safety_items.length}건 —{' '}
                            {d.permit.safety_items.slice(0, 4).map(s => s.name).join(', ')}
                            {d.permit.safety_items.length > 4 ? ' …' : ''}
                          </p>
                        </div>
                      )}

                      {kind === 'tbm' && (
                        <div className="text-xs space-y-1">
                          <p className="text-ink-muted">
                            {(d.tbms || []).length}일치 · 실시일{' '}
                            {(d.tbms || []).map(t => t.tbm_date).filter(Boolean)[0] || '?'}
                            {(d.tbms || []).length > 1 ? ` ~ ${(d.tbms || []).map(t => t.tbm_date).filter(Boolean).slice(-1)[0]}` : ''}
                          </p>
                          <ul className="space-y-0.5">
                            {(d.tbms || []).slice(0, 5).map((t, ii) => (
                              <li key={ii} className="text-ink">
                                <span className="text-ink-muted">{t.tbm_date}</span>{' '}
                                {t.work_description || t.title}
                                {t.safety_measures.length > 0 && (
                                  <span className="text-ink-muted"> — {t.safety_measures.slice(0, 2).join(', ')}</span>
                                )}
                              </li>
                            ))}
                            {(d.tbms || []).length > 5 && (
                              <li className="text-ink-muted">… 외 {(d.tbms || []).length - 5}일</li>
                            )}
                          </ul>
                          <p className="text-[11px] text-amber-600 dark:text-amber-400">
                            가져온 TBM은 기록으로만 저장되며 <strong>법정 교육시간으로 인정되지 않습니다</strong>
                            (실제 출석을 확인할 수 없기 때문입니다).
                          </p>
                        </div>
                      )}

                      {kind === 'site_overview' && (
                        <div className="space-y-2 text-xs">
                          {(d.profile?.equipment?.length || 0) > 0 && (
                            <div>
                              <p className="text-ink-muted mb-1">
                                기계·기구·설비 {d.profile!.equipment!.length}개
                                <span className="text-ink-faint"> — 반영하면 장비 대장에 등록됩니다</span>
                              </p>
                              <div className="flex flex-wrap gap-1.5">
                                {d.profile!.equipment!.map((e, i) => (
                                  <span key={`${e.name}${i}`}
                                        className="rounded-full border border-surface-border bg-surface-raised px-2 py-0.5 text-ink">
                                    🔧 {e.name}{e.quantity ? ` ×${e.quantity}` : ''}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          {(d.profile?.chemicals?.length || 0) > 0 && (
                            <div>
                              <p className="text-ink-muted mb-1">
                                유해화학물질 {d.profile!.chemicals!.length}종
                                <span className="text-ink-faint"> — 유해인자 대장에 등록되고 MSDS 의무가 점등됩니다</span>
                              </p>
                              <div className="flex flex-wrap gap-1.5">
                                {d.profile!.chemicals!.map((c, i) => (
                                  <span key={`${c.name}${i}`}
                                        className="rounded-full border border-surface-border bg-surface-raised px-2 py-0.5 text-ink">
                                    🧪 {c.name}{c.monthly_amount ? ` · 월 ${c.monthly_amount}` : ''}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          <p className="text-[11px] text-ink-faint">
                            사업장 개요는 위험성평가 항목이 아니라 <strong className="text-ink">사업장 프로필</strong>로
                            반영됩니다 — 이후 문서 생성 시 실제 보유 설비·물질이 자동으로 반영됩니다.
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
          <p className="text-xs text-ink-muted">
            지금은 <strong className="text-ink">위험성평가 · JSA · 작업허가서 · 사업장 개요</strong>를 가져올 수 있습니다.
            TBM·안전일지는 지난 기록이라 가져오지 않습니다 — 이 서비스에서 매일 새로 쓰시면 자동으로 집계됩니다.
            교육 이수·MSDS·보호구 대장은 각각의 전용 화면에서 가져올 수 있습니다.
          </p>
          <label className="flex items-center gap-2 text-sm text-ink">
            <input type="checkbox" checked={buildProfile} disabled={phase === 'applying'}
                   onChange={e => setBuildProfile(e.target.checked)} />
            사업장 프로필 자동 구축 — 문서 속 장비·보호구를 추출해 이후 문서 생성에 활용
          </label>
          {pendingConfirm > 0 && (
            <p className="text-xs text-amber-600 dark:text-amber-400">
              허가기간을 읽지 못한 작업허가서 {pendingConfirm}건이 있습니다. 종료된 서류인지
              진행 중인지 골라 주세요 — 진행 중으로 두면 마감 알림이 계속 울립니다.
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="ghost" disabled={phase === 'applying'} onClick={onClose}>취소</Button>
            <Button disabled={phase === 'applying' || selectedDocs.length === 0 || pendingConfirm > 0}
                    onClick={apply}>
              {phase === 'applying'
                ? <span className="flex items-center gap-2"><Spinner size="sm" /> 반영 중…</span>
                : `문서 ${selectedDocs.length}건 · 내용 ${totalItems}건 반영`}
            </Button>
          </div>
        </div>
      )}

      {phase === 'done' && result && (
        <div className="space-y-4">
          {/* 일부 실패를 성공 화면에 묻지 않는다 — 사용자는 전부 들어간 줄 안다. */}
          {!!result.failed?.length && (
            <div className="rounded-xl border border-red-500/40 bg-red-500/5 p-4">
              <p className="text-sm font-semibold text-red-600 dark:text-red-400">
                문서 {result.failed.length}건은 저장하지 못했습니다
              </p>
              <ul className="mt-1.5 space-y-1 text-xs text-ink-muted">
                {result.failed.slice(0, 5).map((f, i) => (
                  <li key={`${f.filename}-${i}`} className="truncate">
                    · {f.filename}{f.error ? ` — ${f.error}` : ''}
                  </li>
                ))}
                {result.failed.length > 5 && (
                  <li>… 외 {result.failed.length - 5}건</li>
                )}
              </ul>
              <p className="mt-1.5 text-xs text-ink-muted">
                나머지는 정상 반영됐습니다. 실패한 문서만 다시 올려 주세요.
              </p>
            </div>
          )}
          <div className={`rounded-xl border p-4 ${
            result.failed?.length ? 'border-surface-border bg-surface-raised'
                                  : 'border-emerald-500/40 bg-emerald-500/5'}`}>
            <p className="text-sm font-semibold text-ink flex items-center gap-2">
              <FiCheck className="text-emerald-500" />
              {result.failed?.length ? '일부 반영 완료' : '이관 완료'}
            </p>
            <p className="text-sm text-ink-muted mt-1">
              {Object.entries(result.counts || {})
                .filter(([, v]) => v > 0)
                .map(([k, v]) => `${KIND_LABEL[k] || k} ${v}건`).join(' · ') || '문서 0건'}
              {/* 사업장 개요만 반영한 경우 '신규 작업 0개' 문구는 실패처럼 읽힌다. */}
              {Object.entries(result.counts || {}).some(([k, v]) => k !== 'site_overview' && v > 0) ? (
                <>{' · '}신규 작업 {result.tasks_created}개가 등록됐습니다. 작업별로 묶여 있어
                작업 보드에서 한눈에 보이고, 사고 반영·상시평가·증빙 PDF·개선조치 추적이 함께 적용됩니다.</>
              ) : (
                <>{' '}— 설비는 장비 대장에, 화학물질은 유해인자 대장에 등록됐고,
                이후 문서 생성 시 실제 보유 설비·물질이 자동으로 반영됩니다.</>
              )}
            </p>
            {!!result.superseded && result.superseded > 0 && (
              <p className="text-xs text-ink-muted mt-1.5">
                같은 작업의 예전 평가표 {result.superseded}건은 <strong>이전 판</strong>으로
                정리했습니다. 이력은 그대로 보존되고 재평가 기한은 최신본 하나만 계산됩니다.
              </p>
            )}
            <p className="text-xs text-ink-muted mt-1.5">
              가져온 문서는 <strong>이관본</strong>으로 표시됩니다. 작업 보드에서 하인리히 형식으로
              새로 만들면 법령 근거·유사사고·보유 장비까지 반영된 문서가 됩니다.
            </p>
            <p className="text-xs text-ink-muted mt-1.5">
              잘못 가져오셨다면 <strong>24시간 안에는 삭제</strong>할 수 있습니다.
              그 뒤에는 법정 보존기간(3년) 동안 지울 수 없으니 지금 확인해 주세요.
            </p>
          </div>
          {result.profile && (result.profile.equipment.length > 0 || result.profile.ppe.length > 0
            || (result.profile.chemicals?.length ?? 0) > 0) && (
            <div>
              <p className="text-xs font-semibold text-ink-muted mb-1.5">추출된 사업장 프로필 (이후 문서 생성에 활용)</p>
              <div className="flex flex-wrap gap-1.5">
                {result.profile.equipment.map(x => (
                  <span key={`eq${x}`} className="rounded-full bg-surface-raised border border-surface-border px-2 py-0.5 text-xs text-ink">🔧 {x}</span>
                ))}
                {result.profile.ppe.map(x => (
                  <span key={`ppe${x}`} className="rounded-full bg-surface-raised border border-surface-border px-2 py-0.5 text-xs text-ink">🦺 {x}</span>
                ))}
                {(result.profile.chemicals || []).map(x => (
                  <span key={`ch${x}`} className="rounded-full bg-surface-raised border border-surface-border px-2 py-0.5 text-xs text-ink">🧪 {x}</span>
                ))}
              </div>
              {(result.profile.chemicals?.length ?? 0) > 0 && (
                <p className="text-[11px] text-ink-faint mt-1">
                  화학물질은 유해인자 대장에 등록됐습니다 — MSDS 파일을 올리시면 물성까지 자동 분석됩니다.
                </p>
              )}
            </div>
          )}
          {/* ── 진단: 가져온 문서에 무엇이 빠졌는가 ───────────────────────── */}
          <div className="rounded-xl border border-surface-border p-4">
            <div className="flex items-center gap-2">
              <FiSearch className="text-brand-primary shrink-0" />
              <p className="text-sm font-semibold text-ink">가져온 문서 진단</p>
              {diag?.status === 'running' && <Spinner size="sm" />}
            </div>

            {diagErr && <p className="text-xs text-ink-muted mt-2">{diagErr}</p>}

            {!diagErr && !diag && (
              <p className="text-xs text-ink-muted mt-2">
                법령·사고 데이터와 대조해 <strong className="text-ink">빠진 항목</strong>을 찾는 중입니다…
              </p>
            )}

            {diag && (
              <>
                <div className="flex flex-wrap gap-2 mt-3">
                  <span className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-2.5 py-1 text-xs text-amber-700 dark:text-amber-400">
                    법령 미대응 <strong>{diag.summary.legal_missing}</strong>건
                  </span>
                  <span className="rounded-lg border border-rose-500/40 bg-rose-500/10 px-2.5 py-1 text-xs text-rose-700 dark:text-rose-400">
                    미대비 사고 위험 <strong>{diag.summary.accident_gaps}</strong>건
                  </span>
                  <span className="rounded-lg border border-surface-border bg-surface-raised px-2.5 py-1 text-xs text-ink-muted">
                    작업 {diag.summary.tasks}개 분석
                  </span>
                </div>

                {diag.status === 'running' && (
                  <p className="text-xs text-ink-muted mt-2">
                    {diag.steps.filter(s => s.status === 'done').length}/{diag.steps.length}개 작업 완료
                  </p>
                )}

                <div className="mt-3 space-y-2 max-h-[38vh] overflow-y-auto pr-1">
                  {diag.results.map(r => (
                    <div key={r.task_id} className="rounded-lg border border-surface-border px-3 py-2">
                      <p className="text-sm font-semibold text-ink">{r.task_name}</p>

                      {r.legal.available && (r.legal.missing_count || 0) > 0 && (
                        <div className="mt-1.5">
                          <p className="text-xs text-amber-700 dark:text-amber-400">
                            평가표에서 다루지 않은 법정 조치 {r.legal.missing_count}건
                            <span className="text-ink-muted"> (적용 조문 {r.legal.matched_count}건 중)</span>
                          </p>
                          <ul className="mt-1 space-y-0.5">
                            {(r.legal.missing || []).slice(0, 4).map(a => (
                              <li key={a.article_no} className="text-xs text-ink">
                                · {a.article_no} {a.article_title}
                                {String(a.penalty_level || '').includes('criminal') && (
                                  <span className="ml-1 text-rose-600 dark:text-rose-400">형사처벌</span>
                                )}
                              </li>
                            ))}
                            {(r.legal.missing || []).length > 4 && (
                              <li className="text-xs text-ink-muted">
                                … 외 {(r.legal.missing || []).length - 4}건
                              </li>
                            )}
                          </ul>
                        </div>
                      )}
                      {r.legal.available && (r.legal.missing_count || 0) === 0 && (
                        <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1.5">
                          적용 법령 {r.legal.matched_count}건은 평가표가 모두 다루고 있습니다.
                        </p>
                      )}

                      {r.accidents.locked ? (
                        <p className="text-xs text-ink-muted mt-1.5">
                          🔒 사고 데이터 대조는 위험도가 가장 높은 작업 1개만 무료로 제공됩니다.
                          <span className="text-ink"> 요금제를 올리면 모든 작업을 분석합니다.</span>
                        </p>
                      ) : r.accidents.available && (r.accidents.gap_count || 0) > 0 ? (
                        <div className="mt-1.5">
                          <p className="text-xs text-rose-700 dark:text-rose-400">
                            유사사고 {r.accidents.cases}건 대조 — 평가표가 대비하지 못한 위험 {r.accidents.gap_count}건
                          </p>
                          <ul className="mt-1 space-y-1">
                            {(r.accidents.gaps || []).slice(0, 3).map((g, i) => (
                              <li key={i} className="text-xs text-ink">
                                · {g.hazard}
                                {g.risk_level && <span className="text-ink-muted"> ({g.risk_level})</span>}
                                {g.measures?.[0] && (
                                  <span className="block text-ink-muted pl-2">→ {g.measures[0]}</span>
                                )}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ) : r.accidents.available ? (
                        <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1.5">
                          유사사고 {r.accidents.cases}건과 대조했으나 빠진 위험요인이 없습니다.
                        </p>
                      ) : null}
                    </div>
                  ))}
                </div>

                {diag.status === 'done' && diagGate?.preview && diag.summary.locked_tasks > 0 && (
                  <p className="text-xs text-ink-muted mt-2">
                    작업 {diag.summary.locked_tasks}개는 사고 데이터 대조가 잠겨 있습니다.
                  </p>
                )}
              </>
            )}
          </div>

          <div className="flex justify-end">
            <Button onClick={onClose}>닫기</Button>
          </div>
        </div>
      )}
    </Modal>
  )
}
